import { FunctionComponent, ReactNode, useEffect, useLayoutEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";

import { Message } from "primereact/message";

import { useStore } from "../../../stores/StoreContext";

import "./GlobalErrorMessages.scss";

interface IGlobalErrorMessagesProps {
  children?: (content: ReactNode) => ReactNode;
}

const RawGlobalErrorMessages: FunctionComponent<IGlobalErrorMessagesProps> = (props: IGlobalErrorMessagesProps) => {
  const { t } = useTranslation();
  const { globalErrorsStore } = useStore();
  const errorContainerRef = useRef<HTMLDivElement | null>(null);
  const hasErrors = globalErrorsStore.hasErrors;

  useEffect(() => {
    if (!hasErrors) {
      globalErrorsStore.setErrorContainerHeight("0px");
    }
  }, [hasErrors]);

  useLayoutEffect(() => {
    if (!hasErrors) {
      return;
    }

    const updateHeight = () => {
      const rect = errorContainerRef.current?.getBoundingClientRect();
      globalErrorsStore.setErrorContainerHeight(`${rect?.height ?? 0}px`);
    };

    updateHeight();

    if (!errorContainerRef.current || typeof ResizeObserver === "undefined") {
      return;
    }

    const observer = new ResizeObserver(() => updateHeight());
    observer.observe(errorContainerRef.current);

    return () => observer.disconnect();
  }, [hasErrors, globalErrorsStore.currentErrors.length]);

  if (!hasErrors) {
    return;
  }

  props.children ??= content => content;

  return props.children(
    <div className="global-errors" ref={errorContainerRef}>
      {globalErrorsStore.currentErrors.map(({ key, data }) => (
        <Message key={key} severity="error" className="global-error" text={t(key, data) as string} />
      ))}
    </div>,
  );
};

export const GlobalErrorMessages = observer(RawGlobalErrorMessages);
